import { useEffect, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { HELP_TEXTS } from '@/lib/help-texts';

/**
 * Botón "?" de ayuda contextual: círculo discreto que al clic abre un popover
 * con la explicación en lenguaje llano (textos centralizados en help-texts.ts,
 * referenciados por clave). Si la clave no existe, no renderiza nada — nunca
 * rompe una pantalla por falta de texto.
 */
export function HelpTip({ helpKey }: { helpKey: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const text = HELP_TEXTS[helpKey];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!text) return null;

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Ayuda: ${text.title}`}
        aria-expanded={open}
        className="rounded-full p-0.5 text-slate-400 transition-colors hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:text-brand-300"
      >
        <HelpCircle className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute left-0 top-7 z-50 w-72 rounded-lg border border-surface-border bg-white p-3 text-left shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{text.title}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{text.body}</p>
        </div>
      )}
    </div>
  );
}
